/**
 * Visualizer Mode Registrations
 *
 * Bridges IVisualizerTool implementations (from ToolRegistry) into the
 * VisualizerModeRegistry by creating adapter components that translate
 * VisualizerModeProps → VisualizerRenderContext → tool.render().
 *
 * Import this module for side effects — it registers all visualizer modes
 * on first load.
 */

import React, { useEffect,useRef, useState } from 'react';

import { ToolRegistry } from '@/engine/ToolRegistry';
import type { IVisualizerTool, VisualizerRenderContext } from '@/interfaces/ITool';
import { VISUALIZER_MANIFEST } from '@/lib/visualizerManifest';
import {
  type VisualizerModeProps,
  VisualizerModeRegistry,
} from '@/lib/VisualizerModeRegistry';

// ─── Generic adapter: wraps a VisualizerTool.render() in a React component ──

function createToolAdapter(toolId: string): React.FC<VisualizerModeProps> {
  const Adapter: React.FC<VisualizerModeProps> = ({
    data,
    modeState,
    onModeStateChange,
    editMode,
    onEdit,
    theme,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState({ width: 800, height: 600 });

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const obs = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setSize({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      });
      obs.observe(el);
      return () => obs.disconnect();
    }, []);

    const tool = ToolRegistry.get(toolId);
    if (!tool || tool.type !== 'visualizer') {
      return (
        <div className="p-4 text-sm text-muted-foreground">
          Visualizer tool not found: {toolId}
        </div>
      );
    }

    const vizTool = tool as IVisualizerTool;
    const context: VisualizerRenderContext = {
      input: data,
      config: modeState ?? {},
      onConfigChange: onModeStateChange,
      containerRef: containerRef as React.RefObject<HTMLDivElement>,
      containerWidth: size.width,
      containerHeight: size.height,
      theme,
      editMode,
      onEdit,
    };

    return (
      <div ref={containerRef} className="flex-1 overflow-auto min-h-0 h-full">
        {vizTool.render(context)}
      </div>
    );
  };

  Adapter.displayName = `VisualizerAdapter(${toolId})`;
  return Adapter;
}

// ─── Register all manifest entries as modes ─────────────────────────────────
// Register unconditionally — tool lookup happens at render time in the adapter.
// This avoids import-order issues where ToolRegistry may not be populated yet.

for (const entry of VISUALIZER_MANIFEST) {
  if (VisualizerModeRegistry.has(entry.mode)) continue; // don't double-register

  const compat = entry.compatibleTypes;

  VisualizerModeRegistry.register({
    id: entry.mode,
    label: entry.label,
    icon: entry.icon,
    canRender: (dataType) => {
      if (!dataType) return true; // no data yet — show all modes
      if (compat === 'all') return true;
      return compat.includes(dataType.semanticType);
    },
    component: createToolAdapter(entry.toolId),
    editable: entry.editable,
    ...(entry.panes ? { panes: [...entry.panes] } : {}),
    ...(entry.headerActions ? { headerActions: [...entry.headerActions] } : {}),
  });
}
